/**
 * Background GPS Tracking Service
 * Tracks user location every 5 minutes and sends to backend
 * Admin-only visibility for tracking all reps
 */

import api from '../utils/api';

// Lazy loaders for Capacitor plugins
let _Capacitor = null;
let _Geolocation = null;

const loadCapacitor = async () => {
    if (_Capacitor) return _Capacitor;
    try {
        const mod = await import(/* webpackIgnore: true */ '@capacitor/core');
        _Capacitor = mod.Capacitor;
        return _Capacitor;
    } catch { return null; }
};

const loadGeolocation = async () => {
    if (_Geolocation) return _Geolocation;
    try {
        const mod = await import(/* webpackIgnore: true */ '@capacitor/geolocation');
        _Geolocation = mod.Geolocation;
        return _Geolocation;
    } catch { return null; }
};

// Tracking state
let trackingInterval = null;
let isTracking = false;
const TRACKING_INTERVAL = 5 * 60 * 1000; // 5 minutes

/**
 * Start background GPS tracking
 */
export const startBackgroundTracking = async () => {
    if (isTracking) {
        return true;
    }

    try {
        // Request permission first
        const permission = await requestLocationPermission();
        if (!permission) {
            console.error('[GPS] Permission denied');
            return false;
        }

        // Get initial location
        await sendLocationToServer();

        // Start interval tracking
        trackingInterval = setInterval(async () => {
            await sendLocationToServer();
        }, TRACKING_INTERVAL);

        isTracking = true;
        localStorage.setItem('gps_tracking_enabled', 'true');
        return true;
    } catch (error) {
        console.error('[GPS] Failed to start tracking:', error);
        return false;
    }
};

/**
 * Stop background GPS tracking
 */
export const stopBackgroundTracking = () => {
    if (trackingInterval) {
        clearInterval(trackingInterval);
        trackingInterval = null;
    }
    isTracking = false;
    localStorage.setItem('gps_tracking_enabled', 'false');
};

/**
 * Check if tracking is enabled
 */
export const isTrackingEnabled = () => {
    return isTracking || localStorage.getItem('gps_tracking_enabled') === 'true';
};

/**
 * Request location permission
 */
export const requestLocationPermission = async () => {
    try {
        const Cap = await loadCapacitor();
        const Geo = await loadGeolocation();
        if (Cap && Cap.isNativePlatform() && Geo) {
            const status = await Geo.checkPermissions();
            if (status.location !== 'granted') {
                const request = await Geo.requestPermissions();
                return request.location === 'granted';
            }
            return true;
        } else {
            // Web: Use navigator
            const result = await navigator.permissions.query({ name: 'geolocation' });
            return result.state === 'granted' || result.state === 'prompt';
        }
    } catch (error) {
        console.error('[GPS] Permission check failed:', error);
        return false;
    }
};

/**
 * Get current position
 */
export const getCurrentPosition = async () => {
    try {
        const Cap = await loadCapacitor();
        const Geo = await loadGeolocation();
        if (Cap && Cap.isNativePlatform() && Geo) {
            const position = await Geo.getCurrentPosition({
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            });
            return {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                timestamp: new Date().toISOString()
            };
        } else {
            // Web fallback
            return new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        resolve({
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                            accuracy: position.coords.accuracy,
                            timestamp: new Date().toISOString()
                        });
                    },
                    reject,
                    { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
                );
            });
        }
    } catch (error) {
        console.error('[GPS] Failed to get position:', error);
        return null;
    }
};

/**
 * Send location to server
 */
export const sendLocationToServer = async () => {
    try {
        const position = await getCurrentPosition();
        if (!position) {
            console.warn('[GPS] No position available');
            return false;
        }

        // Get user info from localStorage
        const userStr = localStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : null;

        if (!user?.id) {
            console.warn('[GPS] No user logged in');
            return false;
        }

        const Cap = await loadCapacitor();
        const locationData = {
            user_id: user.id,
            user_name: user.name || user.username,
            latitude: position.latitude,
            longitude: position.longitude,
            accuracy: position.accuracy,
            timestamp: position.timestamp,
            device_info: {
                platform: Cap ? Cap.getPlatform() : 'web',
                is_native: Cap ? Cap.isNativePlatform() : false
            }
        };

        await api.post('/gps-logs', locationData);

        // Also update live location cache for real-time map
        try {
            await api.post('/live-locations/update', {
                latitude: position.latitude,
                longitude: position.longitude,
                accuracy: position.accuracy
            });
        } catch (liveErr) {
            // Non-critical - live map update failed but GPS log was saved
            console.warn('[GPS] Live location update failed:', liveErr);
        }


        // Cache last location
        localStorage.setItem('last_gps_location', JSON.stringify(locationData));

        return true;
    } catch (error) {
        console.error('[GPS] Failed to send location:', error);
        // Cache for later sync
        const pending = JSON.parse(localStorage.getItem('pending_locations') || '[]');
        const position = await getCurrentPosition();
        if (position) {
            pending.push({ ...position, pending: true });
            localStorage.setItem('pending_locations', JSON.stringify(pending));
        }
        return false;
    }
};

/**
 * Get all rep locations (Admin only)
 */
export const getAllRepLocations = async () => {
    try {
        const response = await api.get('/gps-logs');
        return response.data;
    } catch (error) {
        console.error('[GPS] Failed to get rep locations:', error);
        return [];
    }
};

/**
 * Initialize tracking on app start
 * Only if previously enabled
 */
export const initializeTracking = async () => {
    const wasEnabled = localStorage.getItem('gps_tracking_enabled') === 'true';
    if (wasEnabled) {
        await startBackgroundTracking();
    }
};

/**
 * Sync pending locations when online
 */
export const syncPendingLocations = async () => {
    const pending = JSON.parse(localStorage.getItem('pending_locations') || '[]');
    if (pending.length === 0) return;



    for (const location of pending) {
        try {
            await api.post('/gps-logs', location);
        } catch {
            // Will retry next time
            return;
        }
    }

    localStorage.setItem('pending_locations', '[]');
};

export default {
    startBackgroundTracking,
    stopBackgroundTracking,
    isTrackingEnabled,
    getCurrentPosition,
    sendLocationToServer,
    getAllRepLocations,
    initializeTracking,
    syncPendingLocations
};
